import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

export function useSearchableContextPicker() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const openDropdown = useCallback(() => {
    setIsOpen(true);
    setSearchQuery('');
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, []);

  const closeDropdown = useCallback((returnFocus = true) => {
    setIsOpen(false);
    setSearchQuery('');
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const toggleDropdown = useCallback(() => {
    if (isOpen) closeDropdown();
    else openDropdown();
  }, [closeDropdown, isOpen, openDropdown]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || !isOpen) return;
    event.preventDefault();
    closeDropdown();
  }, [closeDropdown, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (
        containerRef.current
        && event.target instanceof Node
        && !containerRef.current.contains(event.target)
      ) {
        closeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [closeDropdown, isOpen]);

  return {
    isOpen,
    searchQuery,
    setSearchQuery,
    triggerRef,
    containerRef,
    searchInputRef,
    openDropdown,
    closeDropdown,
    toggleDropdown,
    handleKeyDown,
  };
}
